/**
 * Green Flag — SpotSavedWidget
 *
 * Confirmation card shown on the Event details page once the user has
 * registered for the event. Under the header, one mint panel carries the one
 * thing to do next, the first of:
 *   - the check-in code, in the last few hours (never before)
 *   - Start Sparking, while anyone at the event is left to see
 *   - Invite a Spark, while a mutual Spark isn't already going
 *   - Add to Calendar
 * The other actions that apply sit under the panel as plain text buttons.
 * Add to Calendar and Invite a Spark open bottom drawers (Google Calendar or
 * .ics; search & pick from your mutual pre-event sparks), and the ⋯ overflow
 * hands off to the CancelRegistrationDialog (policy + refund + confirm).
 */

import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Text,
  chakra,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { BottomDrawer } from "../BottomDrawer/BottomDrawer";
import dayjs from "dayjs";
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useReq } from "@larner.dev/use-req";
import {
  MatchesPageData,
  MatchesPageSpark,
  SparkWithRegistrationSummary,
} from "@greenflagdate/shared";
import { apiReq } from "../../lib/apiReq";
import { useLastGood } from "../../lib/useLastGood";
import type { PreSparkState } from "../../lib/usePreSparkState";
import { Icon } from "../Icon/Icon";
import { InviteSparkFlow } from "../InviteSparkFlow/InviteSparkFlow";
import { SparkPickerRow } from "../InviteSparkFlow/InviteSparkPrimitives";
import { CancelRegistrationDialog } from "../CancelRegistrationDialog/CancelRegistrationDialog";
import { CheckInCodeLarge } from "../CheckInCodeLarge/CheckInCodeLarge";

export interface SpotSavedWidgetProps {
  event: SparkWithRegistrationSummary;
  location?: string | null;
  description?: string | null;
  /** Called after the cancellation request succeeds so the parent can refresh. */
  onCancelled: () => void;
  /**
   * The hours right before the event, when the attendee is on their way to
   * the door: the check-in code leads. Before then the code is not shown.
   */
  soon: boolean;
  /**
   * The viewer's check-in code, once it has loaded. `checkedIn` swaps the
   * code for a confirmation.
   */
  checkInCode?: {
    token: string;
    onShow: () => void;
    checkedIn: boolean;
  };
  /** Whether anyone at the event is left for the viewer to Spark. */
  preSpark: PreSparkState;
  onStartSparking: () => void;
}

type PanelAction = "spark" | "invite" | "calendar";
type CardAction = "checkIn" | PanelAction;

/** What the mint panel says above its button, per action it leads with. */
const PANEL: Record<
  PanelAction,
  { icon: ReactNode; heading: string; body: string }
> = {
  spark: {
    icon: <Icon name="heart" boxSize="20px" />,
    heading: "Spark before you go",
    body: "Get a feel for who you might meet before you walk in.",
  },
  invite: {
    icon: <Icon name="spark-filled" boxSize="20px" />,
    heading: "Bring a Spark",
    body: "Invite someone you've Sparked with to come along.",
  },
  calendar: {
    icon: <FontAwesomeIcon icon={faCalendar} />,
    heading: "See you there",
    body: "Save the date so it doesn't sneak up on you.",
  },
};

interface CalendarEvent {
  title: string;
  startsAt: string;
  endsAt: string;
  location?: string | null;
  description?: string | null;
}

const formatGoogleDate = (iso: string) =>
  dayjs(iso).utc().format("YYYYMMDD[T]HHmmss[Z]");

const buildGoogleCalendarUrl = ({
  title,
  startsAt,
  endsAt,
  location,
  description,
}: CalendarEvent) => {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${formatGoogleDate(startsAt)}/${formatGoogleDate(endsAt)}`,
  });
  if (location) params.set("location", location);
  if (description) params.set("details", description);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const escapeIcsText = (value: string) =>
  value.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");

const buildIcsBlob = ({
  title,
  startsAt,
  endsAt,
  location,
  description,
}: CalendarEvent) => {
  const dtStamp = dayjs().utc().format("YYYYMMDD[T]HHmmss[Z]");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Green Flag//Event//EN",
    "BEGIN:VEVENT",
    `UID:${dtStamp}-${Math.random().toString(36).slice(2)}@greenflagdate.com`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${formatGoogleDate(startsAt)}`,
    `DTEND:${formatGoogleDate(endsAt)}`,
    `SUMMARY:${escapeIcsText(title)}`,
  ];
  if (location) lines.push(`LOCATION:${escapeIcsText(location)}`);
  if (description) lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
};

export function SpotSavedWidget({
  event,
  location,
  description,
  onCancelled,
  soon,
  checkInCode,
  preSpark,
  onStartSparking,
}: SpotSavedWidgetProps) {
  const { id: sparkId, title, starts_at: startsAt, ends_at: endsAt } = event;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedSpark, setSelectedSpark] = useState<MatchesPageSpark | null>(
    null,
  );

  const [matchesReq, matchesReqState] = useReq<MatchesPageData>(apiReq);

  // The sparks list decides whether Invite a Spark is offered at all, so it
  // loads with the card, once. A failed load offers nothing to invite on the
  // card; the drawer's error state below offers an explicit retry.
  const loadMatches = useCallback(() => {
    matchesReq.get("/user/matches-page");
  }, [matchesReq]);
  useEffect(() => {
    loadMatches();
  }, [loadMatches]);
  // Held through the refetch after an invite, so the card doesn't blank.
  const matches = useLastGood(matchesReqState.result);

  const calendarPayload = { title, startsAt, endsAt, location, description };

  const handleGoogleCalendar = () => {
    window.open(buildGoogleCalendarUrl(calendarPayload), "_blank", "noopener");
    setCalendarOpen(false);
  };

  const handleIcsDownload = () => {
    const blob = buildIcsBlob(calendarPayload);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "event"}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setCalendarOpen(false);
  };

  // Invitable people = mutual pre-event sparks only. Post-event matches
  // (conversations) are intentionally excluded — the backend only permits
  // inviting a mutual pre-event spark to an event.
  const invitable = useMemo<MatchesPageSpark[]>(
    () => matches?.sparks ?? [],
    [matches],
  );
  // Whether any mutual Spark isn't already going to this event: the drawer
  // then has someone to show (it greys out the ones this event can't take).
  // Null until the sparks list answers; "unknown" when it failed to load, in
  // which case Invite a Spark stays on offer below the panel so its drawer
  // can retry.
  const canInviteHere = useMemo<boolean | "unknown" | null>(() => {
    if (matches) {
      return matches.sparks.some((s) => s.upcomingEvent?.sparkId !== sparkId);
    }
    return matchesReqState.error ? "unknown" : null;
  }, [matches, matchesReqState.error, sparkId]);

  // What the panel leads with, or null while the answer that decides it is
  // still loading (so it never flips from one action to another).
  let primary: CardAction | null = null;
  if (soon) {
    primary = checkInCode ? "checkIn" : null;
  } else if (preSpark === "available") {
    primary = "spark";
  } else if (preSpark === "done" && canInviteHere !== null) {
    primary = canInviteHere === true ? "invite" : "calendar";
  }
  const secondary: PanelAction[] =
    primary === "checkIn"
      ? preSpark === "available" ? ["spark"] : []
      : primary === "spark"
        ? canInviteHere ? ["invite", "calendar"] : ["calendar"]
        : primary === "invite"
          ? ["calendar"]
          : primary === "calendar" && canInviteHere === "unknown"
            ? ["invite"]
            : [];

  const actions: Record<
    PanelAction,
    { label: string; icon: ReactNode; onClick: () => void }
  > = {
    spark: {
      label: "Start Sparking",
      icon: <Icon name="heart" boxSize="14px" />,
      onClick: onStartSparking,
    },
    invite: {
      label: "Invite a Spark",
      icon: <Icon name="spark-filled" boxSize="14px" />,
      onClick: () => setInviteOpen(true),
    },
    calendar: {
      label: "Add to Calendar",
      icon: <FontAwesomeIcon icon={faCalendar} />,
      onClick: () => setCalendarOpen(true),
    },
  };

  const filteredMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invitable;
    return invitable.filter((m) =>
      (m.profile.firstName || "").toLowerCase().includes(q),
    );
  }, [invitable, search]);

  return (
    <>
      <Box
        as="section"
        aria-labelledby="spot-saved-heading"
        borderWidth="1px"
        borderColor="border.brand"
        borderRadius="xl"
        bg="bg.canvas"
        p="1rem"
        w="100%"
        maxW="28rem"
        mx="auto"
        boxShadow="sm"
      >
        <Flex align="flex-start" gap="0.75rem">
          <Flex
            w="2rem"
            h="2rem"
            borderRadius="full"
            bg="bg.brand"
            align="center"
            justify="center"
            color="fg.onBrand"
            flexShrink={0}
          >
            <Icon name="check" boxSize="14px" />
          </Flex>
          <Box flex="1">
            <Text id="spot-saved-heading" fontSize="1rem" fontWeight="bold">
              Your spot is saved
            </Text>
            <Text textStyle="caption" color="fg.muted" mt="0.125rem">
              Confirmation sent to your email
            </Text>
          </Box>
          <chakra.button
            type="button"
            aria-label="More options"
            bg="transparent"
            border="none"
            cursor="pointer"
            color="fg.muted"
            w="2rem"
            h="2rem"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            _hover={{ bg: "bg.subtle" }}
            onClick={() => setMoreOpen(true)}
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </chakra.button>
        </Flex>

        {primary === "checkIn" && checkInCode && (
          <CheckInCodeLarge
            token={checkInCode.token}
            checkedIn={checkInCode.checkedIn}
            onShow={checkInCode.onShow}
          />
        )}
        {primary && primary !== "checkIn" && (
          <Flex
            direction="column"
            gap="0.875rem"
            mt="0.875rem"
            p="1rem"
            borderRadius="md"
            bg="greenLight.subtle"
          >
            <Flex gap="0.75rem" align="flex-start">
              <Box color="gf.green.700" fontSize="1.25rem" lineHeight="1" pt="0.125rem">
                {PANEL[primary].icon}
              </Box>
              <Box>
                <Text textStyle="sectionHeading" color="fg.onTint">
                  {PANEL[primary].heading}
                </Text>
                <Text textStyle="bodySecondary" color="fg.onTint" mt="0.125rem">
                  {PANEL[primary].body}
                </Text>
              </Box>
            </Flex>
            <Button
              w="100%"
              variant="solid"
              colorPalette="greenLight"
              size="lg"
              onClick={actions[primary].onClick}
            >
              {actions[primary].label}
            </Button>
          </Flex>
        )}

        {secondary.length > 0 && (
          <HStack justify="center" gap="0.25rem" mt="0.5rem">
            {secondary.map((action, i) => (
              <Fragment key={action}>
                {i > 0 && (
                  <Box aria-hidden w="1px" h="1.125rem" bg="border.default" />
                )}
                <Button
                  variant="ghost"
                  minH="2.75rem"
                  px="0.875rem"
                  color="gf.green.700"
                  textStyle="caption"
                  onClick={actions[action].onClick}
                >
                  {actions[action].icon}
                  {actions[action].label}
                </Button>
              </Fragment>
            ))}
          </HStack>
        )}
      </Box>

      <BottomDrawer
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        title="Add to Calendar"
      >
        <Flex direction="column" gap="0.75rem">
          <Button
            bg="bg.brand"
            color="fg.onBrand"
            onClick={handleGoogleCalendar}
          >
            Google Calendar
          </Button>
          <Button
            bg="transparent"
            color="fg.brand"
            boxShadow="inset 0 0 0 1px var(--chakra-colors-border-brand)"
            onClick={handleIcsDownload}
          >
            Apple / Outlook (.ics)
          </Button>
        </Flex>
      </BottomDrawer>

      <BottomDrawer
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        title="More options"
      >
        <Button
          w="100%"
          bg="transparent"
          color="fg.danger"
          boxShadow="inset 0 0 0 1px var(--chakra-colors-fg-danger)"
          onClick={() => {
            // Hand off to the confirmation dialog rather than stacking it on
            // top of this drawer.
            setMoreOpen(false);
            setCancelOpen(true);
          }}
        >
          Cancel registration
        </Button>
      </BottomDrawer>

      <CancelRegistrationDialog
        open={cancelOpen}
        sparkId={sparkId}
        title={title}
        startsAt={startsAt}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => {
          setCancelOpen(false);
          onCancelled();
        }}
      />

      <BottomDrawer
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
          setSearch("");
          setSelectedSpark(null);
        }}
        title={selectedSpark ? "Send invite" : "Invite a Spark"}
        minH="32rem"
      >
        {selectedSpark ? (
          <InviteSparkFlow
            spark={selectedSpark}
            fixedEvent={{
              sparkId,
              title,
              activity: title,
              startsAt,
              // SpotSavedWidget only renders once the viewer holds a spot, so
              // the fixed event is always one they're registered for.
              viewerRegistered: true,
            }}
            onInvited={() => {
              // Refresh the sparks list so the just-invited spark shows its
              // "already invited" disabled state if the drawer is reopened.
              matchesReq.get("/user/matches-page");
            }}
            onDone={() => {
              setInviteOpen(false);
              setSearch("");
              setSelectedSpark(null);
            }}
          />
        ) : (
          <>
            <Input
              placeholder="Search your sparks"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              mb="1rem"
            />
            {matchesReqState.loading && !matchesReqState.result ? (
              <Text color="fg.muted">Loading…</Text>
            ) : matchesReqState.error && !matchesReqState.result ? (
              <Flex direction="column" align="center" gap="0.75rem" py="1rem">
                <Text color="fg.muted">Couldn't load your sparks.</Text>
                <Button
                  bg="transparent"
                  color="fg.brand"
                  boxShadow="inset 0 0 0 1px var(--chakra-colors-border-brand)"
                  onClick={loadMatches}
                >
                  Try again
                </Button>
              </Flex>
            ) : filteredMatches.length === 0 ? (
              <Text color="fg.muted">
                {invitable.length === 0
                  ? "You don't have any sparks yet."
                  : "No sparks match your search."}
              </Text>
            ) : (
              <Flex direction="column" gap="0.5rem">
                {filteredMatches.map((m) => (
                  <SparkPickerRow
                    key={m.profile.id}
                    spark={m}
                    onPick={() => setSelectedSpark(m)}
                  />
                ))}
              </Flex>
            )}
          </>
        )}
      </BottomDrawer>
    </>
  );
}
